// Internal product-usage analytics — a thin, fire-and-forget event tracker.
//
// This is deliberately separate from:
//   - error tracking (GlitchTip via `captureError` in @/lib/sentry), and
//   - the product's end-user "Analytics/Stats" feature (contact/call metrics).
//
// Events are written to the `analytics_events` table and read by a BI tool
// (Metabase) over a read-only DB role. See docs/analytics.md.
//
// PRIVACY: never pass contact PII (emails, names, message bodies) in `properties`.
// Track IDs, counts, and a `source` dimension — not people.
//
// Swapping backends later (e.g. PostHog) means changing only this file; call
// sites keep using `track('event', { ... })`.

import { supabase } from '@/lib/supabase';

// Cache the current user id so tracking never does a per-event auth round-trip.
let currentUserId: string | null = null;
void supabase.auth.getSession().then(({ data }) => {
  currentUserId = data.session?.user?.id ?? null;
});
supabase.auth.onAuthStateChange((_event, session) => {
  currentUserId = session?.user?.id ?? null;
});

// One id per page load, so events from the same visit can be grouped.
const sessionId =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;

// Don't emit during unit tests.
const disabled = import.meta.env.MODE === 'test';

interface TrackProperties {
  /** Where the action originated, e.g. 'email_sidebar' | 'campaigns_tab'. */
  source?: string;
  /** Current workspace id, when readily available at the call site. */
  workspace_id?: string;
  [key: string]: unknown;
}

/**
 * Record a product-usage event. Fire-and-forget: it never blocks the UI and
 * never throws — analytics failures must not break a user action.
 *
 * @example track('campaign_wizard_opened', { source: 'email_sidebar' })
 */
export function track(event: string, properties: TrackProperties = {}): void {
  if (disabled) return;

  const { source, workspace_id, ...rest } = properties;

  void supabase
    .from('analytics_events')
    .insert({
      event,
      user_id: currentUserId,
      workspace_id: workspace_id ?? null,
      source: source ?? null,
      properties: rest,
      session_id: sessionId,
    })
    .then(({ error }) => {
      if (error) {
        // Best-effort: log at debug level and move on. Do not surface to the user.
        console.debug('[analytics] track failed:', event, error.message);
      }
    });
}
